<!-- eslint-disable vue/no-deprecated-v-on-native-modifier -->
<!-- eslint-disable vue/no-deprecated-v-for-template-key -->
<template lang="html">
  <nav v-if="pageRoutes && pageData"
       class="DesktopNaviGrid"
       :class="{
        showNavigation: showNavigation,
      }">
    <!-- Main Navigation -->
    <ul v-for="(route, index) in showRoutes(1)"
        class="main"
        :class="{ 'main-last': index === showRoutes(1).length - 1 }"
        :key="route.id">
      <li :title="route.title"
          @click="toggleNavigation(route.id)">
        {{ route.title }}
      </li>

      <li v-for="subroute in subRoutes(route.id)"
          :key="subroute.id">
        <router-link :to="{ path: subroute.route }"
                     :title="subroute.title"
                     @click.native="closeNavigation">
          {{ subroute.title }}
        </router-link>
      </li>
    </ul>
  </nav>
</template>

<script>
import "@/assets/css/desktop-navigation.css";
import { mapGetters } from "vuex";

export default {
  name: "ElDesktopNavigation",
  data() {
    return {
      nav: null,
      windowWidth: window.innerWidth,
      timeout: null,
      lastScrollY: 0,
      scrollThreshold: 50,
      isDesktopNavOpen: false,
      isScrolling: false,
      isFixedAtTop: false,
      observer: null,
      openNavObserver: null, // Observer for detecting when opened navigation hits top
      fixedTopActivatedAt: null, // Track scroll position when fixed-top was activated
      isTransitioning: false, // Flag to prevent closing navigation during transitions
    };
  },
  computed: {
    ...mapGetters([
      "pageRoutes",
      "pageData",
      "showNavigation",
      "loadedSlideshow",
    ]),
    // Check if foundation header slideshow is present on the page
    hasFoundationSlideshow() {
      return document.querySelector(".foundationHeaderSlideshow") !== null;
    },
  },
  methods: {
    toggleNavigation(id) {
      if (id) {
        this.nav?.classList.toggle("showNavigation");
        this.isDesktopNavOpen = this.nav?.classList.contains("showNavigation");

        // When opening navigation, keep it in its current case and expand in place
        // Don't change positioning - let the navigation expand where it currently is
      }
    },

    closeNavigation() {
      this.$store.state.showNavigation = false;
      this.nav?.classList.remove("showNavigation");
      this.isDesktopNavOpen = false;
    },

    showRoutes(selector) {
      let items = [];

      switch (selector) {
        case 1:
          items = this.pageRoutes.filter(route => route.is_menu_top === 1);
          break;
      }

      return items.sort((a, b) => a.is_order - b.is_order);
    },

    subRoutes(id) {
      const items = this.pageRoutes.filter(route => route.is_menu_sub === id);
      return items.sort((a, b) => a.is_menu_top - b.is_menu_top);
    },

    createObserver() {
      const options = {
        root: null, // viewport
        rootMargin: '0px',
        threshold: [0, 1] // trigger when element enters/exits viewport
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.target === this.nav) {
            const navRect = entry.boundingClientRect;
            const isNavigationOpen = this.nav?.classList.contains("showNavigation");

            // Calculate the actual navigation bar position (not the expanded content)
            let actualNavTop = navRect.top;
            if (isNavigationOpen && (this.isScrolling || this.isStickyAt150)) {
              // Base height is 32px, expanded adds ~148px upwards
              actualNavTop = navRect.bottom - 32;
            }

            const isAtTop = actualNavTop <= 0;

            if ((this.isScrolling || this.isStickyAt150) && isAtTop && !this.isFixedAtTop) {
              // Navigation bar reached top - switch to Case 3
              this.fixedTopActivatedAt = window.scrollY;
              this.isFixedAtTop = true;
              this.isScrolling = false;
              this.isStickyAt150 = false;
              this.nav?.classList.remove("scrolling", "sticky-at-150");
              this.nav?.classList.add("fixed-top");

              // Collapse navigation when it reaches the top (Case 3)
              if (this.nav?.classList.contains("showNavigation")) {
                this.nav?.classList.remove("showNavigation");
                this.isDesktopNavOpen = false;
              }
            }
          }
        });
      }, options);

      if (this.nav) {
        this.observer.observe(this.nav);
      }
    },

    createOpenNavObserver() {
      const options = {
        root: null, // viewport
        rootMargin: '0px 0px 0px 0px',
        threshold: [0, 0.1, 1] // Multiple thresholds for better detection
      };

      this.openNavObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.target === this.nav) {
            const navRect = entry.boundingClientRect;
            const isNavigationOpen = this.nav?.classList.contains("showNavigation");

            if (isNavigationOpen && (this.isScrolling || this.isStickyAt150)) {
              // Check if the top of the expanded navigation hits the top of the browser
              const expandedNavTop = navRect.top;

              if (expandedNavTop <= 0 && !this.isFixedAtTop) {
                // Opened navigation hit the top - switch to Case 3 and collapse
                this.fixedTopActivatedAt = window.scrollY;
                this.isFixedAtTop = true;
                this.isScrolling = false;
                this.isStickyAt150 = false;

                this.nav?.classList.remove("scrolling", "sticky-at-150", "showNavigation");
                this.nav?.classList.add("fixed-top");
                this.isDesktopNavOpen = false;
              }
            }
          }
        });
      }, options);

      if (this.nav) {
        this.openNavObserver.observe(this.nav);
      }
    },

    headerObserver() {
      // No longer needed - using scroll-based position tracking
    },

    createStickyObserver() {
      // No longer needed
    },

    onResize() {
      this.$store.state.showNavigation = false;
      this.nav.style.transition = "none";
      this.nav.style.display = "none";
      this.windowWidth = window.innerWidth;

      clearTimeout(this.timeout);
      this.timeout = setTimeout(this.showNav, 500);
    },

    onScroll() {
      const currentScrollY = window.scrollY;
      const scrollDifference = Math.abs(currentScrollY - this.lastScrollY);

      const isNavigationOpen = this.nav?.classList.contains("showNavigation");
      const isScrollingDown = currentScrollY > this.lastScrollY;

      // Calculate expanded navigation height based on current breakpoint
      const expandedNavHeight = this.windowWidth >= 900 ? 178 + 18 : 148 + 12; // padding-bottom + padding-top

      // Don't close navigation on scroll - let it stay fixed in place when open
      // Only close on large scroll movements when navigation is NOT transitioning between cases
      if (isNavigationOpen && scrollDifference > this.scrollThreshold && !this.isTransitioning && !this.isScrolling) {
        this.nav?.classList.remove("showNavigation");
        this.isDesktopNavOpen = false;
      }

      // Special logic: When navigation is open and fixed (Case 1), and user scrolls down past nav height
      if (isNavigationOpen && !this.isScrolling && !this.isFixedAtTop && isScrollingDown && currentScrollY > expandedNavHeight) {
        this.transitionOpenNavToScrolling();
      }

      // Case switching logic
      if (currentScrollY === 0) {
        // User scrolled back to top
        const hasSlideshow = this.hasFoundationSlideshow;
        
        if (hasSlideshow) {
          // With slideshow present: Keep navigation in Case 2 (scrolling mode)
          // Don't reset to Case 1
          if (!this.isScrolling) {
            this.isScrolling = true;
            this.isFixedAtTop = false;
            this.fixedTopActivatedAt = null;
            this.nav?.classList.remove("fixed-top");
            this.nav?.classList.add("scrolling");
          }
        } else {
          // Without slideshow: Reset to Case 1 (original behavior)
          this.isScrolling = false;
          this.isFixedAtTop = false;
          this.fixedTopActivatedAt = null;
          this.nav?.classList.remove("scrolling", "fixed-top");
        }
      } else if (currentScrollY > 0 && !this.isScrolling && !this.isFixedAtTop && !isNavigationOpen) {
        // User scrolled down from top - switch to Case 2 (scrolling with content)
        // Only apply this when navigation is closed to avoid conflicts with open nav logic
        this.isScrolling = true;
        this.nav?.classList.add("scrolling");
      } else if (currentScrollY > 0 && !this.isScrolling && !this.isFixedAtTop && isNavigationOpen) {
        // User scrolled down from top while navigation is open - keep it in Case 1 initially
      } else if (this.isFixedAtTop && currentScrollY < this.lastScrollY) {
        // User scrolling up from Case 3 - only switch back if we reach the activation position
        if (this.fixedTopActivatedAt !== null && currentScrollY <= this.fixedTopActivatedAt) {
          this.smoothTransitionFromFixedToScrolling();
        }
      }

      this.lastScrollY = currentScrollY;
    },

    transitionOpenNavToScrolling() {
      if (!this.nav) return;

      // Mark as transitioning to prevent other interference
      this.isTransitioning = true;

      // Temporarily disable transitions to prevent jump
      this.nav.style.transition = "none";

      // Switch to scrolling mode while keeping navigation open
      this.isScrolling = true;
      this.nav.classList.add("scrolling");

      // Use requestAnimationFrame to ensure the class changes are applied
      requestAnimationFrame(() => {
        if (!this.nav) return;

        // Re-enable transitions
        this.nav.style.transition = "padding 0.6s ease-in-out";

        // Clear transitioning flag after a brief delay
        setTimeout(() => {
          this.isTransitioning = false;
        }, 100);
      });
    },

    smoothTransitionFromFixedToScrolling() {
      if (!this.nav) return;

      // Temporarily disable transitions to prevent jump
      this.nav.style.transition = "none";

      // Switch to scrolling mode
      this.isFixedAtTop = false;
      this.nav.classList.remove("fixed-top");
      this.nav.classList.add("scrolling");

      // Use requestAnimationFrame to ensure the class changes are applied
      requestAnimationFrame(() => {
        if (!this.nav) return;

        // Re-enable transitions
        this.nav.style.transition = "padding 1s, transform 0.3s ease-out";

        // Reset any inline styles that might interfere
        this.nav.style.transform = "";

        // The CSS will now handle the positioning
        requestAnimationFrame(() => {
          if (this.nav) {
            // Ensure transitions are back to normal
            this.nav.style.transition = "";
          }
        });
      });
    },

    showNav() {
      if (!this.nav) return;

      Object.assign(this.nav.style, {
        display: "grid",
        transition: "padding 1s"
      });
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.nav = document.querySelector(".DesktopNaviGrid");
      this.lastScrollY = window.scrollY;

      // Initialize navigation state based on slideshow presence and scroll position
      const hasSlideshow = this.hasFoundationSlideshow;
      const currentScrollY = window.scrollY;
      
      if (hasSlideshow) {
        // With slideshow present: Always start with Case 2 (scrolling)
        this.isScrolling = true;
        this.nav?.classList.add("scrolling");
      } else if (currentScrollY === 0) {
        // Without slideshow, at top: Start with Case 1 (fixed at bottom) - this is the default CSS state
        this.isScrolling = false;
        this.isFixedAtTop = false;
        // No need to add classes - default CSS is Case 1
      } else {
        // Without slideshow, already scrolled: Start with Case 2 (scrolling with content)
        this.isScrolling = true;
        this.nav?.classList.add("scrolling");
      }

      // Create intersection observer for Case 3 detection
      this.createObserver();

      // Create intersection observer for opened navigation hitting top
      this.createOpenNavObserver();
    });
    window.addEventListener("resize", this.onResize);
    window.addEventListener("scroll", this.onScroll);
  },

  unmounted() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.openNavObserver) {
      this.openNavObserver.disconnect();
    }
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("scroll", this.onScroll);
  },

  watch: {
    loadedSlideshow() {
      this.showNav();
    },
  },
};
</script>
